import React, { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import billing from 'api/billing';
import { useAppSelector } from 'store/hook';
import { IPaymentHistoryItem, ISubscriptionItem } from 'interfaces';
import SubscriptionsTable from './tables/SubscriptionsTable/SubscriptionsTable';
import PaymentHistoryTable from './tables/PaymentHistoryTable/PaymentHistoryTable';
import RefundModal from './components/RefundModal/RefundModal';
import classes from './Billing.module.scss';

const Billing: React.FC = () => {
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ISubscriptionItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<IPaymentHistoryItem[]>([]);
  const [selectedRefundRecord, setSelectedRefundRecord] = useState<IPaymentHistoryItem | null>(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);

  const { messageApi } = useAppSelector((state) => state.windowState);
  const { t } = useTranslation();

  const fetchSubscriptions = async () => {
    setIsSubscriptionsLoading(true);
    try {
      const response = await billing.getActiveSubscriptions();
      const mapped: ISubscriptionItem[] = (response.data || []).map((item: any, index: number) => ({
        ...item,
        key: item.id || String(index),
        payment_card: item.payment_card_num || item.payment_card || '——',
      }));
      setSubscriptions(mapped);
    } catch {
      messageApi?.error(t('Billing.error.failedGet'));
    } finally {
      setIsSubscriptionsLoading(false);
    }
  };

  const fetchHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const response = await billing.getTariffHistory();
      const mapped: IPaymentHistoryItem[] = (response.data || []).map(
        (item: any, index: number) => {
          const roomName =
            item.rooms?.[0]?.name ||
            item.room?.name ||
            '——';

          return {
            id: item.id || String(index),
            key: item.id || String(index),
            created_at: item.created_at,
            paid_at: item.paid_at,
            room_name: roomName,
            cost:
              typeof item.cost === 'number'
                ? item.cost
                : Number(item.cost) || 0,
            payment_card: item.payment_card_num || item.payment_card || '——',
            payment_status: item.payment_status,
            tariff_name: item.tariff_name,
            rooms: item.rooms,
            quote: item.quote,
          };
        },
      );
      setPaymentHistory(mapped);
    } catch {
      messageApi?.error(t('Billing.error.failedGet'));
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchHistory();
  }, []);

  const handleOpenRefundModal = (record: IPaymentHistoryItem) => {
    setSelectedRefundRecord(record);
    setIsRefundModalOpen(true);
  };

  const handleCloseRefundModal = () => {
    setIsRefundModalOpen(false);
    setSelectedRefundRecord(null);
  };

  return (
    <div className={classes.scheduleWrap}>
      <h2 className={classes.pageTitle}>{t('Billing.pageTitle')}</h2>
      <Tabs
        defaultActiveKey="subscriptions"
        items={[
          {
            label: t('Billing.tabs.subscriptions'),
            key: 'subscriptions',
            children: (
              <SubscriptionsTable
                isLoading={isSubscriptionsLoading}
                data={subscriptions}
                onSubscriptionCancel={fetchSubscriptions}
              />
            ),
          },
          {
            label: t('Billing.tabs.paymentHistory'),
            key: 'paymentHistory',
            children: (
              <PaymentHistoryTable
                isLoading={isHistoryLoading}
                data={paymentHistory}
                onOpenRefundModal={handleOpenRefundModal}
              />
            ),
          },
        ]}
      />
      <RefundModal
        isOpen={isRefundModalOpen}
        onClose={handleCloseRefundModal}
        record={selectedRefundRecord}
        onSuccess={fetchHistory}
      />
    </div>
  );
};

export default Billing;
